import Router from 'next/router';
import {camelCase} from 'lodash';
import qs from 'query-string';
import mixpanel from 'mixpanel-browser';

import {IS_PROD, MIXPANEL_TOKEN, CUSTOMERIO_BROWSER_KEY} from '../constants/config';
import Events from '../constants/events';
import {isBot} from './bots';

const events = Object.keys(Events);
let isInitialized = false;
let gtm;
let customerio;

const updateMixpanel = async (currentUser, hasSubscription) => {
  if (!currentUser) {
    return;
  }

  try {
    mixpanel.identify(currentUser.id);
    mixpanel.people.set({
      // Standard attributes
      $email: currentUser.email,
      $first_name: currentUser.first_name,
      $last_name: currentUser.last_name,
      $avatar: currentUser.profile_img,
      $created: currentUser.created_at,
      // Custom attributes
      uuid: currentUser.uuid,
      ssoProvider: currentUser.sso_provider,
      career: currentUser?.profile?.career_tracks,
      org: currentUser?.org?.slug,
      hasSubscription,
    });
  } catch (e) {
    console.error('[Mixpanel] Failed to identify user:', e);
  }
};

const updateCustomerio = (currentUser) => {
  if (!currentUser || !customerio) {
    return;
  }

  try {
    customerio.identify(String(currentUser.id), {
      email: currentUser.email,
      first_name: currentUser.first_name,
      last_name: currentUser.last_name,
    });
  } catch (e) {
    console.error('[Customer.io] Failed to identify user:', e);
  }
};

const initializeCustomerio = async () => {
  if (!IS_PROD || !CUSTOMERIO_BROWSER_KEY || typeof window === 'undefined') {
    return;
  }

  try {
    // Imported lazily to not break SSR
    const {AnalyticsBrowser} = await import('@customerio/cdp-analytics-browser');
    customerio = AnalyticsBrowser.load({writeKey: CUSTOMERIO_BROWSER_KEY});

    // Track the initial page view, then subsequent client-side route changes
    customerio.page();
    Router.events.on('routeChangeComplete', () => customerio.page());
  } catch (e) {
    console.error('[Customer.io] Failed to initialize:', e);
  }
};

const initializeGTM = () => {
  gtm = (event, data) => {
    if (IS_PROD && window.dataLayer) {
      window.dataLayer.push({event, ...data});
    }
  };
};

const initializeMixpanel = () => {
  if (MIXPANEL_TOKEN) {
    try {
      mixpanel.init(MIXPANEL_TOKEN, {
        debug: !IS_PROD,
        persistence: 'cookie',
        secure_cookie: IS_PROD,
      });
    } catch (e) {
      console.error('[Mixpanel] Failed to initialize:', e);
    }
  }
};

const initializeUserAnalytics = async () => {
  if (isBot()) {
    console.info('[Analytics] Skipping analytics due to user agent');
    return;
  }

  initializeMixpanel();
  initializeGTM();
  await initializeCustomerio();
  isInitialized = true;
};

/**
 * This function is not called directly! Instead it is accessed via
 * the tracker exposed by the useAnalytics() hook.
 * @param {string} eventName Uppercase, snake case name of an event type defined in Events.
 * @param {object} [data] Additional data to send to analytics.
 */
const track = async (eventName, data) => {
  if (!isInitialized) await initializeUserAnalytics();

  if (isBot()) return;

  const {query, url} = qs.parseUrl(Router.asPath);
  const eventData = {...data, path: url, query};

  // Mixpanel tracking
  try {
    // mixpanel-browser iterates objects with a numeric `length` as arrays, emptying the event
    const mixpanelData = {...eventData};
    delete mixpanelData.length;
    mixpanel.track(eventName, mixpanelData);
  } catch (e) {
    console.error('[Mixpanel] Failed to track event:', e);
  }

  // GTM tracking
  gtm(eventName, data);

  // Customer.io tracking
  try {
    customerio?.track(eventName, eventData);
  } catch (e) {
    console.error('[Customer.io] Failed to track event:', e);
  }

  if (!IS_PROD) {
    console.info(`[Analytics] Event '${eventName}' ${data ? 'with data' : ''}`, (data || ''));
  }
};

export const updateUserAnalytics = async (currentUser, hasSubscription) => {
  if (!isInitialized) await initializeUserAnalytics();

  if (isBot()) return;

  await updateMixpanel(currentUser, hasSubscription);
  updateCustomerio(currentUser);
};

// For convenience, we create a camel-cased function for each event name
export const tracker = (properties = {}) => events.reduce((acc, event) => {
  acc[camelCase(event)] = (eventData) => track(Events[event], {...properties, ...eventData});
  return acc;
}, {});

/**
 * To be called after a user has successfully completed a purchased.
 * @param {string|number} total Dollar amount spent on the purchase.
 * @param {string} [email] Email of the purchaser, used for GTM enhanced conversion tracking
 * @param {string} [stripeChargeID] Either from a payment intent or a subscription invoice.
 * @param {string} [stripeCustomerID]
 */
export const trackConversion = async (
  total,
  email,
  stripeChargeID,
  stripeCustomerID,
  sku,
  currency = 'usd',
) => {
  if (!isInitialized) await initializeUserAnalytics();

  track(Events.PAYMENT_SUCCESS, {
    conversionValue: Number(total),
    transactionId: Date.now(),
    transactionTotal: Number(total),
    currencyCode: currency,
    email,
  });

  // GA4 E-commerce tracking
  // Note: Currently assumes one product type per transaction
  gtm('purchase', {
    ecommerce: {
      transaction_id: stripeChargeID,
      value: Number(total),
      currency,
      items: [{
        item_id: sku,
        item_name: sku,
        price: Number(total),
        index: 0,
        quantity: 1,
      }],
    },
  });

  // Tapfiliate conversion events - relies on global 'tap' being defined
  if (typeof window.tap !== 'undefined' && typeof stripeChargeID !== 'undefined' && typeof stripeCustomerID !== 'undefined') {
    window.tap('conversion', stripeChargeID, Number(total), {customer_id: stripeCustomerID, currency});
  } else {
    console.info(`[Tapfiliate] Event 'conversion' with total: ${Number(total)}, charge_id: ${stripeChargeID}, customer_id: ${stripeCustomerID}, currency: ${currency}`);
  }
};
